import type { ComponentPropsWithoutRef } from "react";
import type { Components } from "streamdown";

import { IgTypography } from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";

import { Response } from "@src/components/ai-elements/response";

interface MarkdownTextProps {
  children: string;
  /**
   * Overrides the inherited text color (e.g. the tonal "why it matters"
   * callout color). Defaults to the secondary body color.
   */
  color?: string;
}

/**
 * Forces every link in AI-authored QA prose to open in a new browser tab.
 *
 * QA fix instructions routinely point at the page being edited (e.g. "Go to
 * your Modifications tab") or the customer's storefront; opening those in the
 * same tab throws away the QA report and forces the user to re-open the finding.
 * Setting `target`/`rel` explicitly here standardizes that behavior rather than
 * relying on the markdown renderer's default link handling, which is internal
 * and could change between versions. `node` is dropped so the hast node never
 * leaks onto the DOM anchor.
 */
const markdownComponents: Components = {
  a: ({
    children,
    node: _node,
    ...props
  }: ComponentPropsWithoutRef<"a"> & { node?: unknown }) => (
    <a {...props} rel={"noopener noreferrer"} target={"_blank"}>
      {children}
    </a>
  ),
};

/**
 * Renders AI-generated text as markdown with the surrounding typography.
 *
 * Wraps the shared Response/Streamdown renderer in a `body2` container so inline
 * links become clickable (always opening in a new tab — see `markdownComponents`)
 * and lists/bold/code render, while the text inherits the caller's color and
 * size. Block spacing is handled by the `.ai-chat-text` rules in App.css. Used
 * for every AI-authored prose field in the QA finding report so a URL the model
 * writes anywhere in its prose is always a working link.
 *
 * Streamdown's `linkSafety` interstitial is disabled here: these links live in
 * AI-authored QA prose pointing at our own docs and the customer's storefront,
 * so the confirmation modal adds friction without real safety value.
 */
export const MarkdownText = ({ children, color }: MarkdownTextProps) => {
  const theme = useTheme();

  return (
    <IgTypography
      component={"div"}
      variant={"body2"}
      sx={{
        color: color ?? theme.palette.text.secondary,
        overflowWrap: "anywhere",
        wordBreak: "break-word",
        "& a": {
          color: theme.palette.text.accent,
          textDecoration: "underline",
        },
      }}
    >
      <Response components={markdownComponents} linkSafety={{ enabled: false }}>
        {children}
      </Response>
    </IgTypography>
  );
};
